import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FormSection, RowCard, StatusPill } from '../patterns';
import type { TodaySession } from '../../services/activityService';
import type { Orchard } from '../../services/sprayService';
import { getPrimaryOrchardName } from '../../utils/spraySessionDisplay';
import { formatDateShort } from '../../dateFormat';

interface Props {
  items: TodaySession[];
  loading: boolean;
  orchards?: Orchard[];
}

function statusVariant(status: string): 'ok' | 'warn' | 'danger' | 'info' {
  if (status === 'completed') return 'info';
  if (status === 'in_progress') return 'ok';
  if (status === 'cancelled' || status === 'deleted') return 'danger';
  return 'warn';
}

function sessionLabel(s: TodaySession): string {
  if (s.name && s.name.trim()) return s.name;
  if (typeof s.seq === 'number') return `SES-${s.seq}`;
  return s._id.slice(-6);
}

/**
 * Resolves the date most relevant to the session's lifecycle stage:
 *   - completed     → actual endDate (when work finished)
 *   - in_progress   → actual startDate (when work began)
 *   - everything else (planned, draft, cancelled) → planned date
 *
 * Falls back to the planned date if the lifecycle field is missing.
 */
function effectiveDate(s: TodaySession): string {
  if (s.status === 'completed' && s.endDate) return s.endDate;
  if (s.status === 'in_progress' && s.startDate) return s.startDate;
  return s.date;
}

/**
 * Reduces the per-session combos array to the counts the card needs:
 *   - operators   → total combos (one per operator/machine pairing)
 *   - completed   → combos with status 'completed'
 *   - inProgress  → combos with status 'in_progress'
 *
 * Other statuses (not_started, incomplete, cancelled) contribute only to the
 * operator total; they intentionally don't get their own bucket on this surface.
 */
function comboRollup(combos: NonNullable<TodaySession['combos']>) {
  let completed = 0;
  let inProgress = 0;
  for (const c of combos) {
    if (c.status === 'completed') completed += 1;
    else if (c.status === 'in_progress') inProgress += 1;
  }
  return { operators: combos.length, completed, inProgress };
}

/**
 * Recent spray sessions card on the operator/manager home.
 *
 * Phase 6 / Task 6.16: restyled to compose `<FormSection>` + `<RowCard>` per
 * design-system page patterns. Online-only (manager-side rollup data); no
 * offline cache.
 */
export const RecentSpraySessionsCard: React.FC<Props> = ({ items, loading, orchards = [] }) => {
  const { t } = useTranslation();
  const sorted = [...items].sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)));
  const visible = sorted.slice(0, 6);

  // Render the section even in loading/empty states so the title is
  // discoverable. The count chip shows 0 when items.length === 0, matching
  // the legacy header behaviour.
  return (
    <FormSection
      id="home-recent-spray-sessions"
      title={t('home.recent.sessions.title')}
      description={<span data-testid="recent-spray-sessions-count">{items.length}</span>}
    >
      {loading && (
        <div role="status" style={{ color: 'var(--shell-text-muted)', fontSize: 12, padding: '8px 0' }}>
          {t('common.loading')}
        </div>
      )}
      {!loading && items.length === 0 && (
        <div style={{ color: 'var(--shell-text-muted)', fontSize: 12, padding: '8px 0' }}>
          {t('home.recent.sessions.empty')}
        </div>
      )}
      {!loading && visible.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map(s => {
            const orchardName = getPrimaryOrchardName(s as any, orchards);
            const rollup = s.combos && s.combos.length > 0 ? comboRollup(s.combos) : null;
            return (
              <li key={s._id}>
                <RowCard testId={`row-card-${s._id}`}>
                  <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <span style={{ fontWeight: 600 }}>{sessionLabel(s)}</span>
                      <StatusPill variant={statusVariant(s.status)}>
                        {s.status.replace('_', ' ')}
                      </StatusPill>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--shell-text-muted)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      {orchardName && <span>{orchardName}</span>}
                      <span style={{ marginLeft: 'auto' }}>{formatDateShort(effectiveDate(s))}</span>
                    </div>
                    {rollup && (
                      <div style={{ fontSize: 12, color: 'var(--shell-text-muted)' }}>
                        {t('home.recent.sessions.comboRollup', {
                          operatorCount: rollup.operators,
                          completed: rollup.completed,
                          inProgress: rollup.inProgress,
                        })}
                      </div>
                    )}
                  </div>
                </RowCard>
              </li>
            );
          })}
        </ul>
      )}
      {items.length > 0 && (
        <Link
          to="/protection/sessions"
          className="text-decoration-none"
          style={{ fontSize: 12, color: 'var(--bs-primary, #0D6EFD)', alignSelf: 'flex-start' }}
        >
          {t('home.recent.sessions.viewAll')}
        </Link>
      )}
    </FormSection>
  );
};

export default RecentSpraySessionsCard;
